import { MdReply, MdClose } from "react-icons/md";
import { useState, useRef, useEffect, useContext, createContext } from "react";
import { AppContext } from "@/pages/_app";
import SubReply from "./SubReply";
import Image from "next/image";
import moment from "moment";
import { BsThreeDotsVertical } from "react-icons/bs";
import Link from "next/link";
import { useEditor, EditorContent } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import suggestion from "@/components/Community/Editor/suggestion";
import { FaHeart } from "react-icons/fa";
import { Spinner, Modal, OverlayTrigger } from "react-bootstrap";
import { PostContext } from "@/pages/community/posts/[slug]";
import { callBackendApi } from "@/utils/common";
import Avatar from "react-avatar";
import TiptapLink from "@tiptap/extension-link";
import CustomBody from "@/components/Tiptap/CustomBody";


export const ReplyContext = createContext();

const Reply = ({ replyData, loggedInUser }) => {
  const postContext = useContext(PostContext);
  const { confirmDeleteReply } = postContext;
  const [subReplies, setSubReplies] = useState([]);
  const [reply, setReply] = useState(replyData);
  const [showAllSubReplies, setShowAllSubReplies] = useState(false);
  const [newSubRepliesArray, setNewSubRepliesArray] = useState([]);
  const { backendURL, popoverLoginAlert } = useContext(AppContext);
  const formEditSubReply = useRef();
  const [editSubReplyBody, setEditSubReplyBody] = useState(reply.body);
  const [editSubReplyBodyText, setEditSubReplyBodyText] = useState(reply.bodyText || "");
  const [showEditSubReplyForm, setShowEditSubReplyForm] = useState(false);
  const [loadingEditForm, setLoadingEditForm] = useState(false);
  const [reportType, setReportType] = useState(null);
  const [loadingSubreplies, setLoadingSubreplies] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [loadingLikesModal, setLoadingLikesModal] = useState(false);
  const [likesData, setLikesData] = useState(null);
  const [replyError, setReplyError] = useState(null);
  const [subReplyFormError, setSubReplyFormError] = useState(null);

  // on click likes count button
  const handleShowLikesModal = async () => {
    setLoadingLikesModal(true);
    setShowLikesModal(true);
    // get all likes data of this post
    await callBackendApi({
      method: "get",
      url: `/community/likes/reply/${reply._id}`,
    }).then((response) => {
      setLikesData(response.data.data.map((item) => item.userId));
    });
    setLoadingLikesModal(false);
  };

  // delete sub reply section
  const [loadingDeleteSubReply, setLoadingDeleteSubReply] = useState(false);
  const [showModalDeleteSubReply, setShowModalDeleteSubReply] = useState(false);
  const [currentDeleteSubReplyID, setCurrentDeleteSubReplyID] = useState(null);
  const confirmDeleteSubReply = (_id) => {
    setCurrentDeleteSubReplyID(_id);
    setShowModalDeleteSubReply(true);
  };

  const handleDeleteSubReply = async () => {
    setLoadingDeleteSubReply(true);
    await callBackendApi({
      method: "delete",
      url:
        "/community/posts/" +
        reply.postId +
        "/replies/" +
        currentDeleteSubReplyID,
    }).then((response) => {
      if (response.data.success === true) {
        const replyIndex = subReplies.findIndex(
          (item) => item._id === currentDeleteSubReplyID
        );
        // if this reply dont have any subrelies delete this from list
        if (subReplies[replyIndex].countReplies === 0) {
          subReplies.splice(replyIndex, 1);
        } else {
          subReplies[replyIndex].isDeleted = true;
          subReplies[replyIndex].body = "This reply is deleted by user.";
        }
        reply.countReplies = reply.countReplies - 1;
        reply.recentReplies = reply.recentReplies.filter(
          (item) => item._id !== currentDeleteSubReplyID
        );
        setShowModalDeleteSubReply(false);
      }
    });
    setLoadingDeleteSubReply(false);
  };

  const handleShowEditSubReplyForm = () => {
    setReplyError(null);
    EditSubReplyEditor.commands.setContent(reply.body);
    setEditSubReplyBodyText(EditSubReplyEditor.getText());
    setShowEditSubReplyForm(true);
    EditSubReplyEditor.commands.focus();
  };

  const handleHideEditSubReplyForm = () => {
    setReplyError(null);
    setShowEditSubReplyForm(false);
  };

  const handleSubmitFormEditSubReply = async (e) => {
    e.preventDefault();
    setLoadingEditForm(true);
    try {
      // check if reply body is empty
      if (editSubReplyBodyText === null || editSubReplyBodyText.trim() === "") {
        throw new Error("Reply cannot be empty");
      }
      const formData = new FormData(formEditSubReply.current);
      formData.append("body", JSON.stringify(editSubReplyBody));
      formData.append("bodyText", editSubReplyBodyText);
      const response = await callBackendApi(
        {
          url: "/community/posts/" + reply.postId + "/replies/" + reply._id,
          data: formData,
          method: "put",
        },
        formData
      ).catch(error => {
        // show error message if it come from server
        const errorMessage = error.response.data.data.message || "Something went wrong while submiting reply.";
        throw new Error(errorMessage);
    })
        if (response.data.success === true) {
          const reply = response.data.data;
          reply.recentReplies = [];
          setReply(reply);
          setShowEditSubReplyForm(false);
          EditSubReplyEditor.commands.clearContent(true);
        } else {
          setReplyError("Something went wrong while saving reply.")
        }
    } catch (error) {
      setReplyError(error.message);
    }
    setLoadingEditForm(false);
  };



  const EditSubReplyEditor = useEditor({
    editorProps: {
      attributes: {
        class: "no-focus-outline",
      },
    },
    extensions: [
      Placeholder.configure({
        placeholder: "My Custom Placeholder",
      }),
      Document,
      Paragraph.configure({
        HTMLAttributes: {
          class: "m-0",
        },
      }),
      Text,
      TiptapLink.configure({
        openOnClick: false
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "mention mds-text-primary",
        },
        suggestion,
        renderLabel({ options, node }) {
          return `${options.suggestion.char}${
            node.attrs.id.companyName ?? node.attrs.id
          }`;
        },
      }),
    ],
    onUpdate: ({ editor }) => {
      setEditSubReplyBody(editor.getJSON());
      setEditSubReplyBodyText(editor.getText());
    },
  });

  // reply like section
  const [isLiked, setIsLiked] = useState(reply.isLiked);
  const [loadingLikeBtn, setLoadingLikeBtn] = useState(false);
  const [likeCount, setLikeCount] = useState(reply.countLikes);

  const handleLike = async () => {
    setLoadingLikeBtn(true);
    // get post like status
    await callBackendApi({
      method: "post",
      url: "/community/likes/reply/" + reply._id,
    })
      .then((response) => {
        if (response.data.success == true) {
          setIsLiked(true);
          setLikeCount(likeCount + 1);
        } else {
          setIsLiked(false);
        }
      })
      .catch((error) => {
        alert("error\n" + error);
      });
    setLoadingLikeBtn(false);
  };

  const handleUnLike = async () => {
    setLoadingLikeBtn(true);
    // get post like status
    await callBackendApi({
      method: "delete",
      url: "/community/likes/reply/" + reply._id,
    })
      .then((response) => {
        if (response.data.success == true) {
          setIsLiked(false);
          setLikeCount(likeCount - 1);
        } else {
          setIsLiked(true);
        }
      })
      .catch((error) => {
        alert("error\n" + error);
      });
    setLoadingLikeBtn(false);
  };

  const handleClick = () => {
    if (isLiked) {
      handleUnLike();
    } else {
      handleLike();
    }
  };

  // onclick of replies count button get all subreplies and show
  const handleRepliesCountBtnClick = async () => {
    if (showAllSubReplies) {
      setNewSubRepliesArray([]);
      setShowAllSubReplies(false);
    } else {
      setLoadingSubreplies(true);
      //get sub replies data
      const subRepliesData = await callBackendApi({
        method: "get",
        url: "/community/posts/" + reply.postId + "/replies/" + reply._id,
      }).then((response) => {
        let data = response.data.data;
        // if (data.length > 0) {
        //   data.sort(function (a, b) {
        //     console.log(a, b)
        //     return new Date(b.createdAt) - new Date(a.createdAt);
        //   });
        // }
        return data;
      });
      // reply.countReplies = subRepliesData.length;
      setSubReplies([...subRepliesData]);
      setLoadingSubreplies(false);
      setShowAllSubReplies(true);
    }
  };

  // create new subreply section
  const [loadingNewSubReplyForm, setLoadingNewSubReplyForm] = useState();
  const [showSubReplyForm, setShowSubReplyForm] = useState(false);
  const handleToggleSubReplyForm = () => {
    setSubReplyFormError(null);
    setShowSubReplyForm(!showSubReplyForm);
  };
  const formSubReply = useRef();
  const [NewSubReplyBody, setNewSubReplyBody] = useState(null);
  const [newSubReplyBodyText, setNewSubReplyBodyText] = useState(null);

  const newSubReplyEditor = useEditor({
    editorProps: {
      attributes: {
        class: "no-focus-outline",
      },
    },
    extensions: [
      Placeholder.configure({
        placeholder: "My Custom Placeholder",
      }),
      Document,
      Paragraph.configure({
        HTMLAttributes: {
          class: "m-0",
        },
      }),
      Text,
      TiptapLink.configure({
        openOnClick: false
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "mention mds-text-primary",
        },
        suggestion,
        renderLabel({ options, node }) {
          return `${options.suggestion.char}${
            node.attrs.id.companyName ?? node.attrs.id
          }`;
        },
      }),
    ],
    onUpdate: ({ editor }) => {
      setNewSubReplyBody(editor.getJSON());
      setNewSubReplyBodyText(editor.getText());
    }
  });

  const handleSubmitFormSubReply = async (e) => {
    e.preventDefault();
    setLoadingNewSubReplyForm(true);
    try {
      // check if reply body is empty
      if (newSubReplyBodyText === null || newSubReplyBodyText.trim() === "") {
        throw new Error("Reply cannot be empty");
      }
      const formData = new FormData(formSubReply.current);
      formData.append("body", JSON.stringify(NewSubReplyBody)); 
      formData.append("bodyText", newSubReplyBodyText); // add tiptap body object
      const response = await callBackendApi({
        method: "post",
        url: "/community/posts/" + reply.postId + "/replies/" + reply._id,
        data: formData,
      }).catch(error => {
        // show error message if it come from server
        const errorMessage = error.response.data.data.message || "Something went wrong while submiting reply.";
        throw new Error(errorMessage);
    });
      if (response.data.success === true) {
        let newSubReply = response.data.data;
        newSubReply.isLiked = false;
        newSubReply.recentReplies = [];
        setNewSubRepliesArray([newSubReply._id, ...newSubRepliesArray]);
        reply.countReplies = reply.countReplies + 1;
        if (reply.recentReplies.length < 3) {
          reply.recentReplies.push({ ...newSubReply, userId: loggedInUser });
        }
        setSubReplies([newSubReply, ...subReplies]);
        setShowSubReplyForm(false);
        newSubReplyEditor.commands.clearContent(true);
      } else {
        throw new Error("Something went wrong while submiting reply.");
      }
    } catch (error) {
      setSubReplyFormError(error.message);
    }
    setLoadingNewSubReplyForm(false);
  };

  // report reply section
  const [loadingReportReply, setLoadingReportReply] = useState(false);
  const [showModalReportReply, setShowModalReportReply] = useState(false);
  const handleShowModalReportReply = () => {
    setShowModalReportReply(true);
  };
  const handleHideModalReportReply = () => {
    setShowModalReportReply(false);
  };
  const formReportReply = useRef();
  const handleSubmitFormReportReply = async (e) => {
    e.preventDefault();
    setLoadingReportReply(true);
    const reportFormData = new FormData(formReportReply.current);
    reportFormData.append("replyId", reply._id);
    const response = await callBackendApi({
      method: "post",
      url: "/report",
      data: reportFormData,
    });
    if (response.data.success) {
      setReply({ ...reply, isUserReported: true, flags: { reported: true } });
      handleHideModalReportReply();
    }
    setLoadingReportReply(false);
  };

  useEffect(()=>{
    // load subreplies after page load
    handleRepliesCountBtnClick();
  }, [])

  return (
    <ReplyContext.Provider value={{ confirmDeleteSubReply }}>
      <div className="col">
        <div className="d-flex align-items-start gap-2">
          <div className="rounded-circle mds-bg-white">
            {reply.userId.imageUrl ? (
              <Image
                layout="fixed"
                className="border mds-border-primary-light rounded-circle"
                src={reply.userId.imageUrl}
                width={30}
                height={30}
              />
            ) : (
              <Avatar name={reply.userId.fullName} size={30} round />
            )}
          </div>
          {showEditSubReplyForm ? (
            <div className="flex-fill">
              <form
                className="mds-form"
                ref={formEditSubReply}
                onSubmit={handleSubmitFormEditSubReply}
              >
                <div className="border p-2  mds-rounded-md mds-border-primary-light">
                  <div className="d-flex flex-column align-items-stretch gap-1">
                    <EditorContent
                      className="flex-fill"
                      editor={EditSubReplyEditor}
                    />
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        onClick={handleHideEditSubReplyForm}
                        className="btn py-1 px-4 mds-fw-md  mds-text-primary"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={loadingEditForm}
                        className="text-nowrap py-1 px-4 btn  mds-btn-sm mds-fw-md mds-btn-primary"
                      >
                        Save changes
                      </button>
                    </div>
                  </div>
                </div>
              </form>
              {replyError &&
                <p className="text-danger pt-1 text-align-left">
                  {replyError}
                </p>
              }
            </div>
          ) : (
            <div className="flex-fill   align-self-center mds-border-primary-light">
              <div className="d-flex justify-content-between ">
                  <div className="d-flex align-items-center gap-2 ">
                    <span className="mds-fw-md text-capitalize mds-text-dark text-nowrap">
                      <Link href={`/u/${reply.userId.username}`}>
                        <a className="mds-text-primary">
                          {reply.userId.fullName}
                        </a>
                      </Link>
                    </span>
                </div>
                {loggedInUser && reply.isDeleted !== true && (
                  <div className="mds-dropdown">
                    <button
                      id="dropdownId"
                      type="button"
                      className="mds-fs-5 px-1 mds-rounded-sm mds-btn-light-primary"
                      data-bs-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      <BsThreeDotsVertical />
                    </button>
                    <div className="dropdown-menu" aria-labelledby="dropdownId">
                      {reply.userId.email === loggedInUser?.email &&
                        reply.isDeleted !== true && (
                          <>
                            <button
                              onClick={handleShowEditSubReplyForm}
                              className="dropdown-item"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => confirmDeleteReply(reply._id)}
                              className="dropdown-item"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      {reply.userId.email !== loggedInUser.email &&
                        (reply.isUserReported ? (
                          <span className="dropdown-item-text text-muted">
                            Reported
                          </span>
                        ) : (
                          <button
                            onClick={handleShowModalReportReply}
                            className="dropdown-item"
                          >
                            Report
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
              <p
                className={
                  "m-0 mb-2 mds-rounded mds-fw-sm mds-fs-4 mds-text-secondary  " +
                  (reply.isDeleted &&
                    " mds-bg-primary-muted mt-2 p-2 fst-italic")
                }
              >
                <CustomBody body={reply.body} />
                {reply.edited === true && (
                    <span className="text-muted small"> - Edited </span>
                  )}
              </p>
                
              </div>
              <div className="d-flex justify-content-between align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <OverlayTrigger
                      trigger={!loggedInUser && "click"}
                      placement="top"
                      rootClose
                      overlay={popoverLoginAlert}
                    >
                      <button
                        disabled={loadingLikeBtn}
                        onClick={() => loggedInUser && handleClick()}
                        className={(isLiked && " active ") + " btn pe-0 "}
                      >
                        <FaHeart
                          width={40}
                          className={
                            " mds-btn-icon mds-text-primary " +
                            (isLiked && " active ")
                          }
                        />
                      </button>
                    </OverlayTrigger>
                    <button
                      className="btn ps-0 mds-text-primary"
                      onClick={handleShowLikesModal}
                      disabled={loadingLikeBtn}
                    >
                      {loadingLikeBtn ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <span className="mds-fs-4 mds-fw-md">{likeCount}</span>
                      )}
                    </button>
                  </div>
                  <div>
                    <OverlayTrigger
                      trigger={!loggedInUser && "click"}
                      placement="top"
                      rootClose
                      overlay={popoverLoginAlert}
                    >
                      <button
                        onClick={() => {
                          loggedInUser && handleToggleSubReplyForm();
                          newSubReplyEditor.commands.focus();
                        }}
                        className="mds-fw-sm mds-fs-5 border-0 btn mds-btn mds-btn-transparent-primary d-flex gap-2 align-items-center"
                      >
                        <span style={{ width: "16px", height: "16px" }}>
                          <MdReply
                            style={{
                              width: "16px",
                              height: "16px",
                              padding: "2px",
                            }}
                            className="mds-btn-icon"
                          />
                        </span>
                        <span className="mds-fs-5">Reply</span>
                      </button>
                    </OverlayTrigger>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  {reply.recentReplies.map((item, index) => {
                   return (
                    <div
                      style={{marginLeft : "-5px", zIndex : index}}
                    >
                      {item.userId.imageUrl ? (
                        <span
                        className="d-flex align-items-center"
                        >

                          <Image
                            key={index}
                            layout="fixed"
                            className="border mds-border-primary-light rounded-circle"
                            src={item.userId.imageUrl}
                            width={24}
                            height={24}
                          />
                        </span>
                      ) : (
                        <Avatar
                        className="d-flex align-items-center border mds-border-primary-light"
                          key={index}
                          name={item.userId.fullName}
                          size={24}
                          round
                        />
                      )}
                    </div>
                  );
                  })}
                  <button
                    disabled={loadingSubreplies}
                    style={{ paddingLeft: "0px", marginLeft: "0px" }}
                    onClick={handleRepliesCountBtnClick}
                    className={
                      (!reply.countReplies > 0 && "d-none") +
                      " mds-fs-sm ms-auto btn mds-text-primary text-nowrap ps-2 d-flex align-items-center gap-1"
                    }
                  >
                    {reply.countReplies +
                      (reply.countReplies > 1 ? " Replies" : " Reply")}
                    {loadingSubreplies && (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="mds-text-primary"
                      />
                    )}
                  </button>
                  <span className="text-muted mds-fs-sm ms-lg-2">
                    {moment(reply.createdAt).fromNow("dd")}
                    <span className="d-none d-lg-inline"> ago</span>
                  </span>
                </div>
              </div>

              {showSubReplyForm && (
                <div className="pt-2">
                  <div className="d-flex align-items-start gap-2">
                    <div className="d-none d-lg-inline">
                      {loggedInUser.imageUrl ? (
                        <Image
                          layout="fixed"
                          className="border mds-border-primary-light rounded-circle"
                          src={loggedInUser.imageUrl}
                          width={30}
                          height={30}
                        />
                      ) : (
                        <Avatar name={loggedInUser.fullName} size={30} round />
                      )}{" "}
                    </div>
                    <div className="flex-fill">
                      <form
                        className="mds-form"
                        ref={formSubReply}
                        onSubmit={handleSubmitFormSubReply}
                      >
                        <div className="border p-2 mds-rounded-md mds-border-primary-light">
                          <div className="d-flex flex-column flex-lg-row align-items-end gap-3">
                            <div
                              className="flex-fill w-100"
                            >
                              <EditorContent
                                editor={newSubReplyEditor}
                              />
                            {subReplyFormError &&
                          <p className="text-danger pt-1 m-0 text-align-left">
                            {subReplyFormError}
                          </p>
                        }
                            </div>
                            <button
                              disabled={
                                loadingNewSubReplyForm // disable while sending data
                                || newSubReplyBodyText === null // disable if reply body is not set yet
                                || newSubReplyBodyText.trim() === "" // disable if reply body is empty
                            }
                              className="text-nowrap mds-fs-4 py-1 px-4 btn mds-fs-5 mds-btn-sm mds-fw-md mds-btn-primary"
                            >
                              Post Comment
                            </button>
                          </div>
                        </div>
                        </form>
                      </div>
                  </div>
                        
                </div>
              )}
              <div className="pt-3 ">
                <div className="row row-cols-1 gy-2">
                  {subReplies.map((reply) => {
                    return (
                      <SubReply
                        key={reply._id}
                        className={
                          (showAllSubReplies ||
                            newSubRepliesArray.includes(reply._id) ||
                            " d-none ") + " col "
                        }
                        replyData={reply}
                        loggedInUser={loggedInUser}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        show={showModalDeleteSubReply}
        onHide={() => setShowModalDeleteSubReply(false)}
        contentClassName="mds-rounded border mds-border-primary-light mds-bg-white mds-modal-shadow"
        className="mds-modal pt-5"
      >
        <Modal.Body className="py-4 mds-rounded">
          <div className="d-flex justify-content-center align-items-center mb-3">
            <h5 className="m-0  text-center">Do you want to delete reply ?</h5>
            {/* <button className="btn p-0 px-1 mds-text-primary mds-bg-primary-muted"><MdClose /></button> */}
          </div>
          <div className="d-flex justify-content-center gap-3 align-items-center">
            <button
              disabled={loadingDeleteSubReply}
              type="button"
              onClick={() => setShowModalDeleteSubReply(false)}
              className="btn mds-btn px-5 mds-fw-md mds-btn-outline-primary-light"
            >
              Cancel
            </button>
            <button
              disabled={loadingDeleteSubReply}
              onClick={handleDeleteSubReply}
              type="button"
              className="btn mds-btn px-5 mds-btn-primary mds-fw-md"
            >
              Delete
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showModalReportReply}
        onHide={handleHideModalReportReply}
        contentClassName="mds-rounded border mds-border-primary-light mds-bg-white mds-modal-shadow"
        className="mds-modal pt-5"
      >
        <Modal.Body className="mds-rounded">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="m-0  text-center">Report an issue</h5>
            <button
              onClick={handleHideModalReportReply}
              className="btn p-0 px-1 mds-text-primary mds-bg-primary-muted"
            >
              <MdClose />
            </button>
          </div>
          <p className="m-0 py-3 text-muted">
            Help us understand the problem. What is wrong with this reply ?
          </p>
          <form
            className="mds-form"
            ref={formReportReply}
            onSubmit={handleSubmitFormReportReply}
          >
            {[
              "Illegal services",
              "Hate",
              "Misinformation",
              "Spam",
              "Others",
            ].map((item) => (
              <div key={item} className="form-check">
                <input
                  onClick={() => setReportType(item)}
                  className="form-check-input"
                  type="radio"
                  value={item}
                  name="type"
                  id={item}
                  required
                />
                <label className="form-check-label" htmlFor={item}>
                  {item}
                </label>
              </div>
            ))}
            <input
              className="form-control my-2"
              type={reportType === "Others" ? "text" : "hidden"}
              name="comment"
              placeholder="Mention the reason here"
              required
            />
            <div className="d-grid mt-3">
              <button
                disabled={loadingReportReply}
                className="btn mds-btn btn-lg mds-btn-primary mds-fw-md"
              >
                Report
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showLikesModal}
        onHide={() => setShowLikesModal(false)}
        contentClassName="mds-rounded border mds-border-primary-light mds-bg-white mds-modal-shadow"
        className="mds-modal pt-5"
      >
        <Modal.Body className="mds-rounded">
          <div className="card bg-transparent border-0">
            {loadingLikesModal ? (
              <div className="d-flex justify-content-center align-items-center gap-3">
                <Spinner animation="border" className="mds-text-primary" />
                <span className="mds-fw-4 mds-fw-lg">Getting Likes ...</span>
              </div>
            ) : (
              <>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="m-0 mds-fs-4 mds-fw-md text-center">
                      {likesData?.length} people liked it
                    </h5>
                    <button
                      onClick={() => setShowLikesModal(false)}
                      className="btn p-0 px-1 mds-text-primary mds-bg-primary-muted"
                    >
                      <MdClose />
                    </button>
                  </div>
                </div>
                <div className="list-group list-group-flush mds-list-group-flush">
                  {likesData?.map((item) => (
                    <div key={item._id} className="list-group-item">
                      <div className="d-flex justify-content-start align-items-center gap-3">
                        {item.imageUrl ? (
                          <Image
                            layout="fixed"
                            className="border mds-border-primary-light rounded-circle"
                            src={item.imageUrl}
                            width={40}
                            height={40}
                          />
                        ) : (
                          <Avatar name={item.fullName} size={40} round />
                        )}
                        <span className="mds-fs-4 text-capitalize text-nowrap">
                          <Link href={`/u/${item.username}`}>
                            <a className="mds-text-primary">
                              {item.fullName}
                            </a>
                          </Link>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </ReplyContext.Provider>
  );
};

export default Reply;
